import React, {useState} from 'react';
import {classNames} from '@utils/class-names';
import {ReactComponent as ChevronDownIcon} from '@assets/icons/chevron-down.svg';
import {ReactComponent as CheckmarkIcon} from '@assets/icons/checkmark.svg';
import {ReactComponent as EyeIcon} from '@assets/icons/eye.svg';
import {ReactComponent as EyeSlashIcon} from '@assets/icons/eye-slash.svg';
import {ReactComponent as ArticlesIcon} from '@assets/icons/articles.svg';
import AppButton from "@common/components/app-button";
import Popover from "@common/components/popover";
import {ArticleListRequestParams, Visibility} from "../../core/models/interfaces/article-list-request-params.interface";

interface ArticleFilterProps {
    onSelect?: (value: ArticleListRequestParams) => void;
    className?: string;
    searchParams?: ArticleListRequestParams;
}

const ArticleFilter: React.FC<ArticleFilterProps> = (props) => {
    const {
        onSelect,
        className = '',
        searchParams
    } = props;
    const [selectedValue, setSelectedValue] = useState(searchParams?.visibility);

    const options: Array<{ label: string, value: Visibility, icon: any }> = [
        {label: 'Wszystkie artykuły', value: 'all', icon: ArticlesIcon},
        {label: 'Opublikowane', value: 'visible', icon: EyeIcon},
        {label: 'Ukryte', value: 'hidden', icon: EyeSlashIcon},
    ];

    const selectedOption = options.find((opt) => opt.value === selectedValue) || options[0];

    const handleSelect = (value: Visibility) => {
        setSelectedValue(value);
        onSelect?.({
            ...searchParams,
            visibility: value,
        });
    };

    const dropdownContent = (
        <div className="w-fit bg-white rounded-2xl shadow-lvl-1 border border-outline-mild overflow-hidden">
            <div className="py-2">
                {options.map((option) => {
                    const isSelected = option.value === selectedValue;
                    const IconComponent = option.icon;

                    return (
                        <button
                            key={option.value}
                            onClick={() => handleSelect(option.value)}
                            className={classNames(
                                'flex items-center w-full px-4 py-2 text-sm text-on-surface',
                                'hover:bg-on-surface/5 transition-colors duration-150 pr-[56px] whitespace-nowrap',
                                isSelected ? 'bg-success/10 hover:!bg-success/20 font-semibold' : ''
                            )}
                            role="menuitem"
                        >
                            {isSelected ? (
                                <CheckmarkIcon className="min-w-4 w-4 h-4 mr-3 text-primary"/>
                            ) : IconComponent ? (
                                <IconComponent className="min-w-4 w-4 h-4 mr-3 text-on-surface"/>
                            ) : (
                                <span className="min-w-4 w-4 h-4 mr-3"/>
                            )}
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className={classNames('w-fit', className)}>
            <Popover
                content={dropdownContent}
                preferredPosition="bottom"
                offset={4}
            >
                <AppButton
                    type="button"
                    className="bg-surface-level-1 w-fit items-center justify-center max-h-[32px] rounded-lg border border-outline !py-0 h-[32px]"
                >
                    <div className="flex items-center justify-center">
                        <span className="mr-2 text-sm font-semibold">{selectedOption?.label}</span>
                        <ChevronDownIcon className="transition-transform duration-200"/>
                    </div>
                </AppButton>
            </Popover>
        </div>
    );
};

export default ArticleFilter;
